import { isRecord, dateValueToDate, type DateValue } from "../../types/data";
import type { ProfileUser } from "../../types/profile";
import type { InterfacePreferences } from "../../utility/interfacePreferences";
import type { useProfileAccess } from "../../hooks/useProfileAccess";
import type { WelcomeStep } from "../WelcomeImportModal";
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch as useDispatch } from "../../store/hooks";
import { useAuthState } from "react-firebase-hooks/auth";
import LeftPanel from "../InsertElements/LeftPanel";
import TopPanel from "../InsertElements/TopPanel";
import { UserRatingsProvider } from "../../context/UserRatingsContext";
import { WatchHistoryProvider } from "../../context/WatchHistoryContext";
import { LevelUpProvider } from "../../context/LevelUpContext";
import { ChallengesProvider } from "../../context/ChallengesContext";
import { useFirebase } from "../../context/FirebaseContext";
import { themeColors } from "../../utility/themeColors";
import RatingProgressModeModal from "../RatingProgressModeModal";
import { getCachedOrFetch, invalidateCachedValue } from "../../utility/localDataCache";
import { PUBLIC_PROFILE_STATS_VERSION } from "../../utility/publicProfileStats";
import {
  getUserProfileCacheKey,
  USER_PROFILE_CACHE_OPTIONS
} from "../../utility/profileCache";
import ExperienceMutationRecovery from "../ExperienceMutationRecovery";
import ImportExperienceRecovery from "../ImportExperienceRecovery";
import { isDemoUserId } from "../../utility/demoSession";
import { installRouteIntentPreloading } from "../../utility/routeLoaders";
import { lazyWithChunkRecovery } from "../../utility/chunkLoadRecovery";
import { useI18n } from "../../i18n/useI18n";
import { useUserBackgrounds } from "../../context/UserBackgroundContext";

export interface LayoutProfile extends ProfileUser { themeColor?: string; interfacePreferences?: InterfacePreferences; registrationDate?: DateValue; onboardingGuideSeen?: boolean; onboardingGuidePending?: boolean; }

const WelcomeImportModal = lazy(lazyWithChunkRecovery(() => import("../WelcomeImportModal")));
const OnboardingSpotlight = lazy(lazyWithChunkRecovery(() => import("../OnboardingSpotlight")));
const DemoModeBanner = lazy(lazyWithChunkRecovery(() => import("../DemoModeBanner")));
const WeeklyGameProviders = lazy(lazyWithChunkRecovery(() => import("../WeeklyGameProviders")));

function Layout({ children, userId, profileAccess = null }: { children?: React.ReactNode; userId?: string | null; profileAccess?: ReturnType<typeof useProfileAccess> | null }) {
  const { auth, firestore } = useFirebase();
  const [authUser] = useAuthState(auth);
  const { t } = useI18n();
  const dispatch = useDispatch();
  const backgroundContext = useUserBackgrounds();
  const activateViewedProfileAppearance = backgroundContext?.activateViewedProfileAppearance;
  const updateViewedProfileAppearance = backgroundContext?.updateViewedProfileAppearance;
  const deactivateViewedProfileAppearance = backgroundContext?.deactivateViewedProfileAppearance;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showWelcomeImport, setShowWelcomeImport] = useState(false);
  const [showOnboardingSpotlight, setShowOnboardingSpotlight] = useState(false);
  const [welcomeStep, setWelcomeStep] = useState<WelcomeStep>("welcome");
  const [guidePendingSignal, setGuidePendingSignal] = useState(0);
  const guideHandledUserRef = useRef("");
  const [profileSummaryState, setProfileSummaryState] = useState<{ userId: string; available: boolean | null }>({
    userId: "",
    available: null
  });
  const location = useLocation();
  const activeSection = location.pathname.split("/").filter(Boolean)[1] || "profile";
  const selectedGameMode = new URLSearchParams(location.search).get("tab") || "challenges";
  const needsWeeklyGameProviders =
    ["profile", "estimates", "achievements"].includes(activeSection) ||
    location.pathname.startsWith("/film-page/") ||
    (activeSection === "game" && ["cards", "dungeons"].includes(selectedGameMode));
  const isDemoSession = isDemoUserId(userId) && authUser?.uid === userId;
  const showAdminPrivacyNotice = Boolean(
    profileAccess?.isAdmin && profileAccess?.privacyMode !== "public"
  );
  const isSummaryOnlyPublicRoute = Boolean(
    authUser?.uid &&
    userId &&
    userId !== authUser.uid &&
    ["profile", "friends"].includes(activeSection)
  );
  const ratingsEnabled = !isSummaryOnlyPublicRoute || (
    profileSummaryState.userId === userId &&
    profileSummaryState.available === false
  );

  useEffect(() => {
    return installRouteIntentPreloading();
  }, []);

  useLayoutEffect(() => {
    if (!userId || !activateViewedProfileAppearance) return undefined;
    activateViewedProfileAppearance(userId);
    return () => deactivateViewedProfileAppearance?.(userId);
  }, [
    activateViewedProfileAppearance,
    deactivateViewedProfileAppearance,
    userId
  ]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (!userId) return undefined;
    let active = true;

    const applyProfileSummary = (userData: LayoutProfile = {}) => {
      if (!active) return;
      updateViewedProfileAppearance?.(userId, userData);
      setProfileSummaryState({
        userId,
        available: Number(userData?.publicStats?.version) >= PUBLIC_PROFILE_STATS_VERSION
      });
      const savedTheme = userData.themeColor;
      dispatch({
        type: "setThemeColor",
        payload: savedTheme && Object.prototype.hasOwnProperty.call(themeColors, savedTheme) ? savedTheme : "green"
      });
      if (userId === authUser?.uid) {
        dispatch({
          type: "setInterfacePreferences",
          payload: userData.interfacePreferences || {}
        });
      }
    };

    getCachedOrFetch(
      getUserProfileCacheKey(userId),
      async () => {
        const snapshot = await firestore.collection("users").doc(userId).get();
        return snapshot.exists ? { uid: snapshot.id, ...snapshot.data() } : {};
      },
      {
        ...USER_PROFILE_CACHE_OPTIONS,
        onBackgroundUpdate: applyProfileSummary
      }
    ).then(async (userData) => {
      applyProfileSummary(userData);

      if (!active || userId !== authUser?.uid || isDemoSession) return;
      if (guideHandledUserRef.current === userId) return;

      let legacySeen = false;
      let legacyPending = false;
      try {
        legacySeen = localStorage.getItem(`onboardingGuideSeen_${userId}`) === "true";
        legacyPending = localStorage.getItem(`onboardingGuidePending_${userId}`) === "true";
      } catch {}

      const userDocument = firestore.collection("users").doc(userId);
      if (legacySeen || legacyPending) {
        try {
          await userDocument.set({
            ...(legacySeen ? {
              onboardingGuideSeen: true,
              onboardingGuideSeenAt: new Date(),
              onboardingGuidePending: false
            } : {}),
            ...(legacyPending && !legacySeen ? { onboardingGuidePending: true } : {})
          }, { merge: true });
          try {
            localStorage.removeItem(`onboardingGuideSeen_${userId}`);
            localStorage.removeItem(`onboardingGuidePending_${userId}`);
          } catch {}
          await invalidateCachedValue(getUserProfileCacheKey(userId));
          setGuidePendingSignal((value) => value + 1);
        } catch (error) {
          console.warn("Не удалось перенести статус приветствия:", error);
        }
      }

      // A profile cache can remain valid for several hours. It is useful for
      // rendering, but it must never decide whether a one-time modal is shown:
      // a stale `false` would reopen onboarding on every login. Read this small
      // piece of account state directly from Firestore before opening anything.
      let onboardingUserData: LayoutProfile;
      try {
        const snapshot = await userDocument.get({ source: "server" });
        onboardingUserData = snapshot.exists ? snapshot.data() || {} : {};
      } catch (error) {
        console.warn("Не удалось проверить статус приветствия:", error);
        return;
      }
      if (!active || guideHandledUserRef.current === userId) return;

      if (onboardingUserData.onboardingGuideSeen || legacySeen) {
        guideHandledUserRef.current = userId;
        setShowWelcomeImport(false);
        return;
      }
      if (onboardingUserData.onboardingGuidePending) {
        // The dedicated pending-flow effect opens the guide directly. Do not
        // briefly show the import welcome step first.
        return;
      }

      const createdAt =
        dateValueToDate(onboardingUserData.createdAt) ||
        dateValueToDate(onboardingUserData.registrationDate) ||
        (authUser?.metadata?.creationTime ? new Date(authUser.metadata.creationTime) : null);
      const isNewUser = createdAt
        ? Date.now() - createdAt.getTime() < 7 * 24 * 60 * 60 * 1000
        : false;

      if (
        isNewUser &&
        location.pathname === `/${userId}/profile`
      ) {
        setWelcomeStep("welcome");
        setShowWelcomeImport(true);
      }
    }).catch((error: unknown) => {
      if (active) setProfileSummaryState({ userId, available: false });
      console.error("Не удалось загрузить цветовую схему профиля:", error);
    });
    return () => { active = false; };
  }, [
    authUser?.metadata?.creationTime,
    authUser?.uid,
    dispatch,
    firestore,
    isDemoSession,
    location.pathname,
    updateViewedProfileAppearance,
    userId
  ]);

  useEffect(() => {
    const handleImportComplete = (event: Event) => {
      const detail: unknown = event instanceof CustomEvent ? event.detail : null;
      const eventUserId = isRecord(detail) && typeof detail.userId === "string" ? detail.userId : undefined;
      if (eventUserId && eventUserId !== userId) return;
      const targetUserId = eventUserId || userId;
      if (!targetUserId || targetUserId !== authUser?.uid || isDemoSession) return;
      if (guideHandledUserRef.current === targetUserId) return;
      firestore.collection("users").doc(targetUserId).set({
        onboardingGuidePending: true
      }, { merge: true }).then(() => {
        invalidateCachedValue(getUserProfileCacheKey(targetUserId));
        setGuidePendingSignal((value) => value + 1);
      }).catch((error: unknown) => console.warn("Не удалось сохранить ожидание приветственного гайда:", error));
    };

    window.addEventListener("onboarding-import-complete", handleImportComplete);
    return () => window.removeEventListener("onboarding-import-complete", handleImportComplete);
  }, [authUser?.uid, firestore, isDemoSession, userId]);

  useEffect(() => {
    if (!userId || userId !== authUser?.uid || isDemoSession) return undefined;
    if (location.pathname !== `/${userId}/profile`) return undefined;
    if (guideHandledUserRef.current === userId) return undefined;

    let active = true;
    const userDocument = firestore.collection("users").doc(userId);
    userDocument.get({ source: "server" }).then((snapshot) => {
      if (!active) return;
      const userData = snapshot.exists ? snapshot.data() || {} : {};
      if (userData.onboardingGuideSeen) {
        guideHandledUserRef.current = userId;
      }
      if (!userData.onboardingGuidePending) return;
      if (userData.onboardingGuideSeen) {
        userDocument.set({
          onboardingGuidePending: false
        }, { merge: true }).then(() => {
          invalidateCachedValue(getUserProfileCacheKey(userId));
        }).catch((error: unknown) => {
          console.warn("Не удалось сбросить ожидание приветственного гайда:", error);
        });
        setShowWelcomeImport(false);
        return;
      }
      if (guideHandledUserRef.current === userId) return;

      setWelcomeStep("guide");
      setShowWelcomeImport(true);
    }).catch((error: unknown) => console.warn("Не удалось загрузить статус приветственного гайда:", error));

    return () => { active = false; };
  }, [authUser?.uid, firestore, guidePendingSignal, isDemoSession, location.pathname, userId]);

  const markGuideSeen = async ({ step }: { step?: WelcomeStep } = {}) => {
    const shouldStartSpotlight = step === "guide" || welcomeStep === "guide";
    guideHandledUserRef.current = userId || "";
    setShowWelcomeImport(false);
    if (shouldStartSpotlight) {
      window.setTimeout(() => setShowOnboardingSpotlight(true), 160);
    }
    if (!userId || isDemoSession) return;

    // Keep a durable browser fallback until the server confirms the write. If
    // the tab closes or the connection drops at this exact moment, the next
    // login migrates this marker instead of reopening onboarding.
    try {
      localStorage.setItem(`onboardingGuideSeen_${userId}`, "true");
      localStorage.removeItem(`onboardingGuidePending_${userId}`);
    } catch {}

    if (userId === authUser?.uid) {
      try {
        await firestore.collection("users").doc(userId).set({
          onboardingGuideSeen: true,
          onboardingGuideSeenAt: new Date(),
          onboardingGuidePending: false
        }, { merge: true });
        await invalidateCachedValue(getUserProfileCacheKey(userId));
        try {
          localStorage.removeItem(`onboardingGuideSeen_${userId}`);
        } catch {}
      } catch (error) {
        console.error("Не удалось сохранить статус приветствия:", error);
      }
    }
  };

  const finishSpotlight = useCallback(() => {
    setShowOnboardingSpotlight(false);
  }, []);

  return (
    <UserRatingsProvider userId={userId} enabled={ratingsEnabled}>
      <WatchHistoryProvider userId={userId} enabled={ratingsEnabled}>
        <ChallengesProvider userId={userId}>
          <LevelUpProvider>
              <ExperienceMutationRecovery userId={userId} />
              <ImportExperienceRecovery userId={userId} />
              <div className="layout">
              {showAdminPrivacyNotice && (
                <div className="profile-access-notice profile-access-notice--admin" role="status">
                  {t("Просмотр закрытого профиля в административном режиме")}
                </div>
              )}
              <TopPanel
                userId={userId}
                profileAccess={profileAccess}
                isMobileMenuOpen={isMobileMenuOpen}
                onToggleMobileMenu={toggleMobileMenu}
                onCloseMobileMenu={closeMobileMenu}
              />
              <main className="main">
                {needsWeeklyGameProviders ? (
                  <Suspense fallback={(
                    <>
                      <LeftPanel
                        userId={userId}
                        isMobileMenuOpen={isMobileMenuOpen}
                        onCloseMobileMenu={closeMobileMenu}
                      />
                      {children}
                    </>
                  )}>
                    <WeeklyGameProviders userId={userId}>
                      <LeftPanel
                        userId={userId}
                        isMobileMenuOpen={isMobileMenuOpen}
                        onCloseMobileMenu={closeMobileMenu}
                      />
                      {children}
                    </WeeklyGameProviders>
                  </Suspense>
                ) : (
                  <>
                    <LeftPanel
                      userId={userId}
                      isMobileMenuOpen={isMobileMenuOpen}
                      onCloseMobileMenu={closeMobileMenu}
                    />
                    {children}
                  </>
                )}
              </main>
              <RatingProgressModeModal />
              {showWelcomeImport && (
                <Suspense fallback={null}>
                  <WelcomeImportModal
                    initialStep={welcomeStep}
                    onClose={markGuideSeen}
                    onFinish={markGuideSeen}
                    onStartImport={() => setShowWelcomeImport(false)}
                  />
                </Suspense>
              )}
              {showOnboardingSpotlight && (
                <Suspense fallback={null}>
                  <OnboardingSpotlight
                    active
                    onFinish={finishSpotlight}
                  />
                </Suspense>
              )}
              {isDemoSession && (
                <div className="demo-mode-banner-slot">
                  <Suspense fallback={null}>
                    <DemoModeBanner userId={userId} />
                  </Suspense>
                </div>
              )}
              </div>
          </LevelUpProvider>
        </ChallengesProvider>
      </WatchHistoryProvider>
    </UserRatingsProvider>
  );
}

export default Layout;
